// @ts-check

/**
 * @type {string[]} User-type object types for ZTNA rules
 */
export const ZTNA_USER_TYPES = [
  'cloud_ssl_vpn',
  'portal',
  'user',
  'user-group',
  'sase_device',
];

/**
 * @type {string[]} Application-type object types for ZTNA rules
 */
export const ZTNA_APP_TYPES = ['ztna_application'];

/**
 * @type {string[]} All ZTNA-related object types
 */
export const ZTNA_ALL_TYPES = [...ZTNA_USER_TYPES, ...ZTNA_APP_TYPES];

export const ZTNA_APPLICATIONS_FETCH_LIMIT = 1000;

/**
 * @type {number} Max applications per ZTNA rule; 0 means unlimited (picker convention)
 */
export const MAX_ZTNA_APPLICATIONS_PER_RULE = 0;

const WEB_SERVICE_NAMES = ['http', 'https'];

/**
 * Mirrors the backend ZtnaApplication#requires_url?: a TCP service named HTTP or HTTPS.
 *
 * @param {{service?: {object_type?: string, name?: string}}} app
 * @returns {boolean}
 */
export const isWebZtnaApplication = (app) =>
  app?.service?.object_type === 'tcp_service' &&
  WEB_SERVICE_NAMES.includes(app?.service?.name?.toLowerCase());

/**
 * True when web and non-web applications are both present. Apps without a service are
 * ignored; the backend decides on those.
 *
 * @param {Array<Object>} apps
 * @returns {boolean}
 */
export const hasMixedZtnaApplicationTypes = (apps) => {
  const withService = apps.filter((app) => app.service);
  return (
    withService.some(isWebZtnaApplication) &&
    withService.some((app) => !isWebZtnaApplication(app))
  );
};

/**
 * Computes the allowed types for a ZTNA field based on the other field's selections.
 * Implements mutual exclusion: if the other field has an app, this field is locked to user types,
 * and vice versa. If the other field is empty, all types are allowed.
 *
 * @param {Array<{type: string}>} thisFieldItems - Items currently in this field
 * @param {Array<{type: string}>} otherFieldItems - Items currently in the other field
 * @returns {string[]} Allowed types for this field
 */
export const computeAllowedTypes = (thisFieldItems, otherFieldItems) => {
  if (!otherFieldItems || otherFieldItems.length === 0) {
    return ZTNA_ALL_TYPES;
  }

  const otherHasApp = otherFieldItems.some(
    (item) => item.type === 'ztna_application'
  );

  if (otherHasApp) {
    return ZTNA_USER_TYPES;
  }

  return ZTNA_APP_TYPES;
};

/**
 * Computes whether a ZTNA rule is reverse based on source items.
 * A rule is reverse when the source field contains a ztna_application.
 *
 * @param {Array<{type: string}>} sourceItems - Items in the source field
 * @param {Array<{type: string}>} destItems - Items in the destination field
 * @returns {boolean} True if the rule is reverse
 */
export const computeReverse = (sourceItems, destItems) => {
  return sourceItems.some((item) => item.type === 'ztna_application');
};

/**
 * Whether a ZTNA rule must masquerade its sources: forward pass rules with portal users,
 * because application servers have no route back to the portal pool.
 *
 * @param {Array<Object>} sourceItems - Items from the source field
 * @param {Array<Object>} destItems - Items from the destination field
 * @param {string} action - Rule action ('pass' or 'drop')
 * @param {number[]} portalUserIds - Ids of the portal users already stored on the rule
 * @param {number[]} portalGroupIds - Ids of the portal user groups already stored on the rule
 * @returns {boolean} True if masquerade SNAT is mandatory
 */
export const requiresMasqueradeSnat = (
  sourceItems,
  destItems,
  action,
  portalUserIds,
  portalGroupIds = []
) => {
  if (action === 'drop' || computeReverse(sourceItems, destItems)) return false;

  return sourceItems.some(
    (item) =>
      (item.object_type === 'user' &&
        (item.portal || portalUserIds.includes(item.id))) ||
      (item.group_type === 'user' &&
        (item.portal || portalGroupIds.includes(item.id)))
  );
};

/**
 * Extracts and categorizes ZTNA parameters from the unified source/destination fields.
 * Determines which field has the application and which has user items, then categorizes
 * user items into users, vpnTunnels, and devices.
 *
 * @param {Array<Object>} sourceItems - Items from the source field
 * @param {Array<Object>} destItems - Items from the destination field
 * @returns {{reverse: boolean, ztnaApplicationIds: number[], userItems: Object[], vpnTunnels: Object[], devices: Object[]}}
 */
export const extractZtnaParams = (sourceItems, destItems) => {
  const reverse = computeReverse(sourceItems, destItems);
  const appItems = reverse ? sourceItems : destItems;
  const userFieldItems = reverse ? destItems : sourceItems;

  const ztnaApplicationIds = appItems
    .filter((item) => item.type === 'ztna_application')
    .map((item) => item.id);

  const userItems = [];
  const vpnTunnels = [];
  const devices = [];

  userFieldItems.forEach((item) => {
    if (item.object_type || item.group_type) {
      userItems.push(item);
    } else if (item.tunnel_type) {
      vpnTunnels.push(item);
    } else if ((item.alias_name || item.name) && item.role) {
      devices.push(item);
    } else {
      userItems.push(item);
    }
  });

  return {
    reverse,
    ztnaApplicationIds,
    userItems,
    vpnTunnels,
    devices,
  };
};

/**
 * Whether a ZTNA field is the applications field: it holds an app, it is locked to apps,
 * or both fields are empty and defaultWhenEmpty is set.
 *
 * @param {Array<{type: string}>} thisFieldItems
 * @param {Array<{type: string}>} otherFieldItems
 * @param {boolean} defaultWhenEmpty
 * @returns {boolean}
 */
export const isApplicationField = (thisFieldItems, otherFieldItems, defaultWhenEmpty) =>
  thisFieldItems.some((item) => item.type === 'ztna_application') ||
  computeAllowedTypes(thisFieldItems, otherFieldItems) === ZTNA_APP_TYPES ||
  (defaultWhenEmpty && thisFieldItems.length === 0 && otherFieldItems.length === 0);
